import { track } from '@amplitude/analytics-browser'
import Bugsnag from '@bugsnag/js'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { fetchCalendar } from 'api'
import AgendaPane from 'Components/AgendaPane'
import Calendar, {
  type CalendarProps,
  type CalendarEvent,
} from 'Components/Calendar'
import CalendarModal, { type SelectedTile } from 'Components/CalendarModal'
import Modal from 'Components/Common/Modal'
import PoweredBySked from 'Components/Common/PoweredBySked'
import type { TabViewValue } from 'Components/Navbar'
import {
  ExclamationMarkRoundedIcon,
  TileApprovedIcon,
  TileRejectedIcon,
  TilePendingIcon,
} from 'Icons'
import { normalizeCalendarEvents } from 'normalizer'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'ui/tooltip'
import { ensureError } from 'utils/ensureError'
import getReviewKeyFromURL from 'utils/getReviewKeyFromURL'
import type { PreviewVariant } from 'utils/postPreviewVariant'

interface CalendarPageProps {
  setTabView: (view: TabViewValue) => void
  isReadOnly: boolean
  previewVariant: PreviewVariant
}

const CalendarPage: React.FC<CalendarPageProps> = ({
  setTabView,
  isReadOnly,
  previewVariant,
}) => {
  const reviewKey = getReviewKeyFromURL(window.location.href)

  const [currentSelectedDate, setCurrentSelectedDate] = useState<Date>(
    import.meta.env.VITE_IS_PLAYWRIGHT === 'true'
      ? new Date('12/01/2022') // set a specified date for integration test
      : new Date()
  )

  const [nextTileIndex, setNextTileIndex] = useState<number>(0)

  const [calendarEvents, setCalendarEvents] = useState<
    CalendarProps['events'] | []
  >([])
  const [selectedTile, setSelectedTile] = useState<SelectedTile | undefined>(
    undefined
  )

  // Agenda pane — opened from a calendar cell's "N more" trigger.
  const [agendaOpen, setAgendaOpen] = useState(false)
  const [agendaDate, setAgendaDate] = useState<Date | undefined>(undefined)
  const [agendaEvents, setAgendaEvents] = useState<CalendarEvent[]>([])

  const fetchCalendarAsync = async (
    reviewKey: string,
    currentSelectedDate: Date
  ) => {
    try {
      const calendarResponse = await fetchCalendar(
        reviewKey,
        currentSelectedDate
      )
      if (calendarResponse) {
        setCalendarEvents(normalizeCalendarEvents(calendarResponse))
      }
    } catch (error) {
      Bugsnag.notify(ensureError(error))
    }
  }

  useEffect(() => {
    fetchCalendarAsync(reviewKey, currentSelectedDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTileClick = (selectedTile: SelectedTile) => {
    setSelectedTile(selectedTile)

    // for navigation purpose
    setNextTileIndex(
      calendarEvents.findIndex((event) => event.id === selectedTile.id)
    )

    track('ext-cal-post-click')
  }

  const handleShowMore = (date: Date, events: CalendarEvent[]) => {
    setAgendaDate(date)
    setAgendaEvents(events)
    setAgendaOpen(true)
  }

  const handleReviewPost = () => {
    track('ext-cal-post-review')

    setTabView('approval')

    if (selectedTile?.id) {
      const postId = selectedTile.id

      setTimeout(() => {
        const toBeSelectedPostElem = document.querySelector(
          `[data-postid="${postId}"`
        ) as HTMLDivElement

        toBeSelectedPostElem?.click()

        toBeSelectedPostElem?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        })
      }, 300)
    }
  }

  const handleSelectedDateChange = (changedDate: Date) => {
    setCurrentSelectedDate(changedDate)
    fetchCalendarAsync(reviewKey, changedDate)
  }

  const handleNavigationArrowClick =
    (direction: 'left' | 'right', currentId: string) => () => {
      let nextTileIndex = calendarEvents.findIndex(
        (event) => event.id === currentId
      )
      if (direction === 'right') {
        nextTileIndex++
      } else nextTileIndex--

      setNextTileIndex(nextTileIndex)

      const {
        id,
        start,
        tileData: { time, platform, status },
      } = calendarEvents.find((_e, index) => index === nextTileIndex)!

      if (id) {
        setSelectedTile({
          id: id.toString(),
          date: format(new Date(start), 'MMM d yyyy'),
          time,
          platform,
          status,
        })
      }
    }

  // Section title (matches the "Upcoming Posts"/"Post Preview" heading style)
  // + a small (i) tooltip trigger to its right, rendered into the calendar
  // header row.
  const legend = (
    <div className="flex items-center gap-2">
      <h2 className="font-gotham text-xl font-extrabold text-neutral-800">
        Upcoming Posts
      </h2>
      {!isReadOnly && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid="calendar-legend-tooltip"
                className="grid cursor-help place-items-center text-neutral-400 [&_svg]:size-4"
              >
                <ExclamationMarkRoundedIcon />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[320px]">
              <p className="m-0">
                In the &lsquo;Calendar&rsquo; view you&rsquo;ll be able to see
                upcoming posts. Click on the posts to see a full preview. Posts
                have the following states:
              </p>
              <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
                <li className="flex items-center gap-2.5 [&_svg]:size-[22px]">
                  <TileApprovedIcon />
                  Approved posts.
                </li>
                <li className="flex items-center gap-2.5 [&_svg]:size-[22px]">
                  <TileRejectedIcon />
                  Posts you&rsquo;ve requested changes for.
                </li>
                <li className="flex items-center gap-2.5 [&_svg]:size-[22px]">
                  <TilePendingIcon />
                  Posts that require your review.
                </li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )

  return (
    <div>
      {selectedTile && (
        <Modal
          className="w-[900px]"
          open={!!selectedTile}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTile(undefined)
            }
          }}
        >
          <CalendarModal
            reviewKey={reviewKey}
            selectedTile={selectedTile}
            reviewPostClick={handleReviewPost}
            isReadOnly={isReadOnly}
            navigationArrowClick={handleNavigationArrowClick}
            hideArrowLeft={nextTileIndex === 0}
            hideArrowRight={nextTileIndex === calendarEvents.length - 1}
            previewVariant={previewVariant}
          />
        </Modal>
      )}

      <AgendaPane
        open={agendaOpen}
        onOpenChange={setAgendaOpen}
        date={agendaDate}
        events={agendaEvents}
        onPostClick={handleTileClick}
        isReadOnly={isReadOnly}
      />

      <Calendar
        events={calendarEvents}
        tileClick={handleTileClick}
        onShowMore={handleShowMore}
        defaultSelectedDate={currentSelectedDate}
        onSelectedDateChange={handleSelectedDateChange}
        legend={legend}
        isReadOnly={isReadOnly}
      />
      <PoweredBySked className="mx-auto mb-[30px] mt-[60px] flex justify-center" />
    </div>
  )
}

export default CalendarPage
